import { apiClient, getSession } from "@/auth-client";
import { queryOptions } from "@tanstack/react-query";

export const sessionQueryOptions = queryOptions({
  queryKey: ["session"] as const,
  queryFn: getSession,
});

export const companyQueryOptions = (companyId?: string) =>
  queryOptions({
    queryKey: ["company", companyId] as const,
    queryFn: async () => {
      const result = await apiClient.v1.company[":companyId"].$get({
        param: { companyId: companyId ?? "" },
      });
      if (!result.ok) {
        // User might not be a member of the company.
        throw new Error("Failed to fetch company");
      }
      return result.json();
    },
  });

export const adminDepartmentsQueryOptions = (companyId: string) =>
  queryOptions({
    queryKey: ["admin", "departments", companyId],
    queryFn: async () => {
      const result = await apiClient.v1.admin.departments[":companyId"].$get({
        param: { companyId },
      });
      if (!result.ok) {
        throw new Error("Failed to fetch departments");
      }
      return result.json();
    },
  });

// Query options for fetching departments of a company
export const departmentsQueryOptions = (companyId?: string) =>
  queryOptions({
    queryKey: ["departments", companyId] as const,
    queryFn: async () => {
      const result = await apiClient.v1.company[":companyId"].departments.$get({
        param: { companyId: companyId ?? "" },
      });
      if (!result.ok) {
        throw new Error("Failed to fetch departments");
      }
      return result.json();
    },
  });

export const invitationQueryOptions = (invitationCode: string) =>
  queryOptions({
    queryKey: ["invitation", invitationCode],
    queryFn: async () => {
      const response = await apiClient.v1.invite[":invitationCode"].$get({
        param: { invitationCode },
      });
      if (!response.ok) {
        // User might not be a member of the company.
        throw new Error("Invitation not found or expired");
      }
      const data = await response.json();
      return data.invitation;
    },
  });

// Create query options for fetching forms
export const formsQueryOptions = ({
  companyId,
  departmentId,
}: {
  companyId: string;
  departmentId?: string;
}) =>
  queryOptions({
    queryKey: ["forms", companyId, departmentId],
    queryFn: async () => {
      const result = await apiClient.v1.company[":companyId"].forms.$get({
        param: { companyId },
        query: { departmentId: departmentId ?? undefined },
      });
      if (!result.ok) {
        const error: Error & { status?: number } = new Error(
          result.status === 403
            ? "No tienes permisos para ver estos formularios"
            : "Failed to fetch forms",
        );
        error.status = result.status;
        throw error;
      }
      const apiResponse = await result.json();
      return apiResponse;
    },
  });

// Get specific drill form
export const drillFormQueryOptions = (
  companyId: string,
  teamId: string,
  formId: string,
) =>
  queryOptions({
    queryKey: ["form", companyId, teamId, formId] as const,
    queryFn: async () => {
      const response = await apiClient.v1.company[":companyId"].departments[
        ":departmentId"
      ].drill[":formId"].$get({
        param: {
          companyId,
          departmentId: teamId,
          formId,
        },
      });
      if (!response.ok) {
        const error: Error & { status?: number } = new Error(
          response.status === 403
            ? "No tienes permisos para ver este simulacro"
            : "Failed to fetch drill form",
        );
        error.status = response.status;
        throw error;
      }
      return await response.json();
    },
  });

// Get specific visit form
export const visitFormQueryOptions = (
  companyId: string,
  teamId: string,
  formId: string,
) =>
  queryOptions({
    queryKey: ["form", companyId, teamId, formId] as const,
    queryFn: async () => {
      const response = await apiClient.v1.company[":companyId"].departments[
        ":departmentId"
      ]["hseq-visit"][":formId"].$get({
        param: {
          companyId,
          departmentId: teamId,
          formId,
        },
      });
      if (!response.ok) {
        const error: Error & { status?: number } = new Error(
          response.status === 403
            ? "No tienes permisos para ver esta visita"
            : "Failed to fetch visit form",
        );
        error.status = response.status;
        throw error;
      }
      return await response.json();
    },
  });

// Get specific investigation form
export const investigationFormQueryOptions = (
  companyId: string,
  teamId: string,
  formId: string,
) =>
  queryOptions({
    queryKey: ["form", companyId, teamId, formId] as const,
    queryFn: async () => {
      const response = await apiClient.v1.company[":companyId"].departments[
        ":departmentId"
      ].investigation[":formId"].$get({
        param: {
          companyId,
          departmentId: teamId,
          formId,
        },
      });
      if (!response.ok) {
        const error: Error & { status?: number } = new Error(
          response.status === 403
            ? "No tienes permisos para ver esta investigación"
            : "Failed to fetch investigation form",
        );
        error.status = response.status;
        throw error;
      }
      return await response.json();
    },
  });

// Get specific observation form
export const observationFormQueryOptions = (
  companyId: string,
  teamId: string,
  formId: string,
) =>
  queryOptions({
    queryKey: ["form", companyId, teamId, formId] as const,
    queryFn: async () => {
      const response = await apiClient.v1.company[":companyId"].departments[
        ":departmentId"
      ].observation[":formId"].$get({
        param: {
          companyId,
          departmentId: teamId,
          formId,
        },
      });
      if (!response.ok) {
        const error: Error & { status?: number } = new Error(
          response.status === 403
            ? "No tienes permisos para ver esta observación"
            : "Failed to fetch observation form",
        );
        error.status = response.status;
        throw error;
      }
      return await response.json();
    },
  });

// Get specific management form
export const managementFormQueryOptions = (
  companyId: string,
  teamId: string,
  formId: string,
) =>
  queryOptions({
    queryKey: ["form", companyId, teamId, formId] as const,
    queryFn: async () => {
      const response = await apiClient.v1.company[":companyId"].departments[
        ":departmentId"
      ].management[":formId"].$get({
        param: {
          companyId,
          departmentId: teamId,
          formId,
        },
      });
      if (!response.ok) {
        const error: Error & { status?: number } = new Error(
          response.status === 403
            ? "No tienes permisos para ver esta gestión del cambio"
            : "Failed to fetch management form",
        );
        error.status = response.status;
        throw error;
      }
      return await response.json();
    },
  });

// Get specific mailbox form
export const mailboxFormQueryOptions = (
  companyId: string,
  teamId: string,
  formId: string,
) =>
  queryOptions({
    queryKey: ["form", companyId, teamId, formId] as const,
    queryFn: async () => {
      const response = await apiClient.v1.company[":companyId"].mailbox[
        ":formId"
      ].$get({
        param: {
          companyId,
          formId,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch mailbox form");
      }
      return await response.json();
    },
  });

// Get mailbox forms created by the current user for a department
export const mailboxFormsByUserQueryOptions = (companyId: string) =>
  queryOptions({
    queryKey: ["mailbox", companyId] as const,
    queryFn: async () => {
      const response = await apiClient.v1.company[
        ":companyId"
      ].mailboxs.my.$get({
        param: { companyId },
      });
      if (!response.ok) throw new Error("Failed to fetch mailbox forms");
      return await response.json();
    },
  });

// Create query options for fetching actions
export const actionsQueryOptions = ({
  companyId,
  departmentId,
  formType,
  date,
  limit,
  offset,
}: {
  companyId: string;
  departmentId?: string;
  formType?: string;
  date?: string;
  limit?: number;
  offset?: number;
}) =>
  queryOptions({
    queryKey: [
      "actions",
      companyId,
      departmentId,
      formType,
      date,
      limit,
      offset,
    ] as const,
    queryFn: async () => {
      const result = await apiClient.v1.company[":companyId"].actions.$get({
        param: { companyId },
        query: {
          departmentId: departmentId ?? undefined,
          formType: formType ?? undefined,
          date: date ?? undefined,
          limit: limit?.toString() ?? undefined,
          offset: offset?.toString() ?? undefined,
        },
      });
      if (!result.ok) {
        const error: Error & { status?: number } = new Error(
          result.status === 403
            ? "No tienes permisos para ver estas acciones"
            : "Failed to fetch actions",
        );
        error.status = result.status;
        throw error;
      }
      const apiResponse = await result.json();
      return apiResponse;
    },
  });

// General mailbox list across the company (optional department filter)
export const companyMailboxQueryOptions = (
  companyId: string,
  departmentId?: string,
) =>
  queryOptions({
    queryKey: ["company", "mailbox", companyId, departmentId] as const,
    queryFn: async () => {
      const result = await apiClient.v1.company[":companyId"].mailboxs.$get({
        param: { companyId },
      });
      if (!result.ok) throw new Error("Failed to fetch company mailbox forms");
      return await result.json();
    },
  });

export const departmentUsersQueryOptions = ({
  companyId,
  departmentId,
}: {
  companyId: string;
  departmentId?: string;
}) =>
  queryOptions({
    queryKey: ["users", companyId, departmentId],
    queryFn: async () => {
      const result = await apiClient.v1.company[":companyId"].departments[
        ":departmentId"
      ].users.$get({
        param: { companyId, departmentId: departmentId ?? "" },
      });
      if (!result.ok) {
        throw new Error("Failed to fetch users");
      }
      const apiResponse = await result.json();
      return apiResponse;
    },
  });

// Location query options
export const locationsQueryOptions = (companyId?: string) =>
  queryOptions({
    queryKey: ["locations", companyId] as const,
    queryFn: async () => {
      if (!companyId) return { locations: [] };
      const result = await apiClient.v1.location[":companyId"].locations.$get({
        param: { companyId },
      });
      if (!result.ok) {
        throw new Error("Failed to fetch locations");
      }
      return result.json();
    },
  });

// Zones query options for a specific location
export const zonesQueryOptions = (companyId?: string, locationId?: string) =>
  queryOptions({
    queryKey: ["zones", companyId, locationId] as const,
    queryFn: async () => {
      if (!companyId || !locationId) return { zones: [] };
      const result = await apiClient.v1.location[":companyId"].locations[
        ":locationId"
      ].zones.$get({
        param: { companyId, locationId },
      });
      if (!result.ok) {
        throw new Error("Failed to fetch zones");
      }
      return result.json();
    },
  });

// Job positions query options
export const jobPositionsQueryOptions = (companyId?: string) =>
  queryOptions({
    queryKey: ["jobPositions", companyId] as const,
    queryFn: async () => {
      if (!companyId) return { jobPositions: [] };
      const result = await apiClient.v1.location[":companyId"][
        "job-positions"
      ].$get({
        param: { companyId },
      });
      if (!result.ok) {
        throw new Error("Failed to fetch job positions");
      }
      return result.json();
    },
  });
